import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useCallback, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {DataObjectId, Tag} from 'yougile-model'
import {UiSlot} from '@api/components'
import {ApiTaskInfo} from '@api/components/api-task-info'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {ProjectsFlatTreeDropdownContent} from '@components/common/projects-tree/projects-tree'
import {PinStickersDropdown} from '@components/sticker/pin-sticker-menu'
import {StickerItem} from '@components/sticker/sticker-item'
import {UserAvatar} from '@components/user-avatar'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {UserStickerState} from '@extensions/sticker/views-web/user/user-sticker-state'
import {useTaskInfoLogStore} from '../task-info-log-store'

// eslint-disable-next-line max-lines-per-function
export const TaskInfo = observer(() => {
  const [state, store] = useTaskInfoLogStore()
  const [openMenu, setOpenMenu] = useState<DataObjectId[]>([])

  const onCloseStickerMenu = useCallback((sticker: Tag) => {
    setOpenMenu(prev => prev.filter(id => id !== sticker.id))
  }, [])

  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'current',
    {filter: state.hub?.id},
    () => {
      forceUpdate()
    },
    [state.hub]
  )

  if (!state.hub) {
    return null
  }

  return (
    <>
      <div
        className={cn(
          'task-info__title',
          state.spoilers.info && 'task-info__title--opened'
        )}
        onClick={store.spoilersToggleInfo}
      >
        {state.isRepeatTemplate
          ? Lang.msg.repeat.templateTaskInfo
          : Lang.msg.chat.taskInfo}
      </div>

      {state.spoilers.info && (
        <div className="task-info__cnt">
          {state.isRepeatTemplate && (
            <div className="task-info__repeat">
              <div className="task-info__field">
                {Lang.msg.repeat.nearestRepeat}:{' '}
                {state.repeat.isStopped()
                  ? Lang.msg.repeat.stopped
                  : state.repeat.getNearestRepeat()}
              </div>
              <div className="task-info__field">
                {Lang.msg.repeat.tableConditions}:{' '}
                {state.repeat.isStopped()
                  ? Lang.msg.repeat.stopped
                  : state.repeat.getConditions()}
              </div>

              <div>{Lang.msg.repeat.tplCreateTo}:</div>
              <div className="task-info__location">
                <div
                  className="task-info__location-item"
                  onClick={state.repeat.goToBoard}
                >
                  {state.repeat.getLocationTitle()[0]}
                </div>
                <div className="task-info__location-delimiter">»</div>
                <div
                  className="task-info__location-item"
                  onClick={state.repeat.goToBoard}
                >
                  {state.repeat.getLocationTitle()[1]}
                </div>
                <div className="task-info__location-delimiter">»</div>
                <div
                  className="task-info__location-item"
                  onClick={state.repeat.goToBoard}
                >
                  {state.repeat.getLocationTitle()[2]}
                </div>
              </div>
            </div>
          )}
          {!state.isPrivate && (
            <div>
              <div className="task-info__subtitle">
                {Lang.msg.chat.whoSection}
              </div>
              {!state.isRepeatTemplate && (
                <div className="task-info__field">
                  <div className="task-info__created">
                    {Lang.msg.created}:
                    <span className="task-info__created-user">
                      <UserAvatar
                        className="task-info__creator"
                        user={store.creator()}
                        size={20}
                        showStatus
                        withUserInfo
                      />
                    </span>
                    <span className="task-info__created-time">
                      {Time.formatDateTime(state.hub.timestamp)}
                    </span>
                  </div>
                </div>
              )}
              <div className="task-info__field">
                <div className="task-info__assigned">
                  {Lang.msg.chat.assigned}:
                  <div className="task-sticker-item user">
                    <UserStickerState
                      users={state.assigned.users}
                      projectId={store.projectId()}
                      options={state.assigned.available}
                      onChoose={state.assigned.onChoose}
                      withButton
                    />
                  </div>
                </div>
              </div>
              <div className="task-info__field">
                <div className="task-info__assigned">
                  {Lang.msg.chat.responsible}:
                  <div className="task-sticker-item user">
                    <UserStickerState
                      users={state.responsible.users}
                      projectId={store.projectId()}
                      options={state.assigned.available}
                      onChoose={state.responsible.onChoose}
                      singleMode
                      withButton
                    />
                  </div>
                </div>
              </div>
              {!state.isRepeatTemplate && (
                <div className="task-info__field">
                  <div className="task-info__created">
                    {Lang.msg.chat.assignedBy}:
                    {store.assignedBy().map(userId => (
                      <span className="task-info__created-user" key={userId}>
                        <UserAvatar
                          className="task-info__creator"
                          userId={userId}
                          size={20}
                          showStatus
                          withUserInfo
                        />
                      </span>
                    ))}
                    {!store.assignedBy.length && (
                      <span className="task-info__created-user">—</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {!state.hiddenLocation && (
            <div>
              <div className="task-info__subtitle">
                {Lang.msg.chat.locationSection}
              </div>

              {!state.noBoard && (
                <div className="task-info__field">
                  <div className="task-info__location">
                    <div
                      className="task-info__location-item"
                      onClick={store.goToBoard}
                    >
                      {state.splitLocationTitle[0]}
                    </div>
                    <div className="task-info__location-delimiter">»</div>
                    <div
                      className="task-info__location-item"
                      onClick={store.goToBoard}
                    >
                      {state.splitLocationTitle[1]}
                    </div>
                    <div className="task-info__location-delimiter">»</div>
                    <div
                      className="task-info__location-item"
                      onClick={store.goToBoard}
                    >
                      {state.splitLocationTitle[2]}
                    </div>

                    {!globals.sharedId && (
                      <div className="flex-full">
                        <div
                          className="task-info__move-to-board"
                          onClick={store.goToBoard}
                        >
                          <span className="task-info__open-icon"></span>
                          {Lang.msg.chat.goToTask}
                        </div>
                        <Dropdown
                          visible={state.chooseLocation.open}
                          onVisibleChange={() =>
                            store.chooseLocationOpen(false)
                          }
                          content={
                            <ProjectsFlatTreeDropdownContent
                              onChange={state.chooseLocation.onDone}
                            />
                          }
                        >
                          <div
                            className="task-info__move-to-board"
                            onClick={() => store.chooseLocationOpen(true)}
                          >
                            <span className="task-info__move-icon"></span>
                            {Lang.msg.move}
                          </div>
                        </Dropdown>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!!store.parentTasks().length && (
                <div className="task-info__field">
                  <span className="text-third-old">
                    {Lang.msg.common.subtask}:&nbsp;
                  </span>
                  {store.parentTasks().map((parent, i) => (
                    <span
                      className="cursor-pointer text-main-old hover:text-action-hover"
                      onClick={() => store.openChat(parent)}
                      key={parent.id}
                    >
                      {parent.title}
                      {i !== store.parentTasks().length - 1 && <span>, </span>}
                    </span>
                  ))}
                </div>
              )}

              {state.noBoard &&
                !state.isPrivate &&
                !store.parentTasks().length && (
                  <div className="task-info__field">
                    <span className="task-info__empty">
                      {Lang.msg.chat.noBoard}
                    </span>
                    <Dropdown
                      visible={state.chooseLocation.open}
                      onVisibleChange={() => store.chooseLocationOpen(false)}
                      content={
                        <ProjectsFlatTreeDropdownContent
                          onChange={state.chooseLocation.onDone}
                        />
                      }
                    >
                      <div
                        className="task-info__move-to-board"
                        onClick={() => store.chooseLocationOpen(true)}
                      >
                        <span className="task-info__move-icon"></span>
                        {Lang.msg.chat.moveToBoard}
                      </div>
                    </Dropdown>
                  </div>
                )}

              {state.isPrivate && (
                <div className="task-info__field">
                  <span className="task-info__empty">
                    {Lang.msg.chat.privateHint}
                  </span>{' '}
                  {!state.isFromPrivateList && (
                    <span
                      className="task-info__move-to-board"
                      onClick={() => store.makePublic()}
                    >
                      {Lang.msg.chat.makePublic}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          {!!state.hub.properties.feedbackUserId && (
            <div>
              <div className="task-info__subtitle">HelpDesk Ticket</div>
              {!!state.hub.properties.feedbackUserName && (
                <div className="task-info__field">
                  <>Name: {state.hub.properties.feedbackUserName}</>
                </div>
              )}

              {!!state.hub.properties.feedbackUserEmail && (
                <div className="task-info__field">
                  <>E-mail: {state.hub.properties.feedbackUserEmail}</>
                </div>
              )}

              {!!state.hub.properties.feedbackTariff && (
                <div className="task-info__field">
                  <>Tariff: {state.hub.properties.feedbackTariff}</>
                </div>
              )}

              {!!state.hub.properties.feedbackUserCount && (
                <div className="task-info__field">
                  <>User count: {state.hub.properties.feedbackUserCount}</>
                </div>
              )}

              {!!state.hub.properties.feedbackBrowser && (
                <div className="task-info__field">
                  <>Browser: {state.hub.properties.feedbackBrowser}</>
                </div>
              )}

              {!!state.hub.properties.feedbackOs && (
                <div className="task-info__field">
                  <>OS: {state.hub.properties.feedbackOs}</>
                </div>
              )}

              <div className="task-info__field">
                Mobile: {state.hub.properties.feedbackIsMobile ? 'yes' : 'no'}
              </div>
            </div>
          )}

          {!state.isFromPrivateList && (
            <div>
              <div className="task-info__subtitle">
                {Lang.msg.chat.stickerSection}
              </div>
              {state.isRepeatTemplate && state.stickerTypes.has('deadline') && (
                <div className="task-info__deadline-repeat-help">
                  {Lang.msg.repeat.deadlineHelp}
                </div>
              )}
              <div className="task-info__field">
                <div className="task-info__stickers">
                  <UiSlot
                    name="task-info-stickers-before"
                    data={{
                      taskId: state.hub?.id
                    }}
                  />
                  {state.stickers
                    .filter(sticker => sticker.title !== 'User')
                    .map(sticker => {
                      return (
                        <StickerItem
                          key={sticker.id}
                          sticker={sticker}
                          location="task"
                          taskId={state.hub?.id}
                          currentValue={
                            state.isRepeatTemplate
                              ? state.hub?.stickers?.[sticker.id]
                              : undefined
                          }
                          menuIsOpened={openMenu.includes(sticker.id)}
                          onCloseMenu={onCloseStickerMenu}
                          draggable
                          withMenu
                          withTooltip
                        />
                      )
                    })}
                  <UiSlot
                    name="task-info-stickers-after"
                    data={{
                      taskId: state.hub?.id
                    }}
                  />
                  {store.canEditStickers() && (
                    <PinStickersDropdown
                      stickers={state.stickerList.allInBoard()}
                      onClickOnSticker={sticker => {
                        state.stickerList.add(sticker)
                        setOpenMenu(prev => [...prev, sticker.id])
                      }}
                    >
                      <PinStickerButton />
                    </PinStickersDropdown>
                  )}
                </div>
              </div>
            </div>
          )}

          <ApiTaskInfo />
        </div>
      )}
    </>
  )
})

export const PinStickerButton = () => {
  return <div className="task-sticker__plus">{Lang.msg.chat.addSticker}</div>
}
